import React from 'react';
import { ColorClassNames, ConstrainMode, DetailsList, IDetailsHeaderProps, IRenderFunction, isIE11, ScrollablePane, SelectAllVisibility, Sticky, StickyPositionType } from '@fluentui/react';
import styles from './StickyHeaderDetailsList.module.css';
import IStickyHeaderDetailsListProps from './dto/IStickyHeaderDetailsListProps';
import isMobileView from '../../../../util/isMobileView';

/**
 * Details list with sticky header
 */
export default class StickyHeaderDetailsList extends React.Component<IStickyHeaderDetailsListProps, {}> {
    /**
     * Max height of the sticky container
     */
    private readonly _maxHeight: number = 610;

    /**
     * Max height of the sticky container for IE11
     */
    private readonly _maxHeightIE11: number = 640;

    /**
     * Pixels added to the height calculation for auto height
     */
    private readonly _autoHeightAdd: number = 50;

    /**
     * Sticky list container
     */
    private _stickyListContainer: HTMLDivElement | null = null;

    /**
     * Renders the detail list
     * @returns Rendered list
     */
    public render(): JSX.Element {
        if (!this.props.items || this.props.items.length === 0 || isMobileView()) {
            return <DetailsList {...this.props}></DetailsList>
        }

        return (
            <div ref={this.setStickyListContainerRef} className={`${styles.detailsListStickyContainer} ${ColorClassNames.whiteBackground}`} style={{ height: this.getStickyContainerHeight() }} onScroll={this.handleScroll}>
                <ScrollablePane className={this.isPreventUncessearyHorizontalScrollNeeded() ? styles.detailsListStickyContainerOverflowXFix : ""}>
                    <DetailsList
                        {...this.props}
                        constrainMode={ConstrainMode.unconstrained}
                        onRenderDetailsHeader={
                            (detailsHeaderProps?: IDetailsHeaderProps, defaultRender?: IRenderFunction<IDetailsHeaderProps>) => {
                                if(detailsHeaderProps && this.props.hideSelectAll) {
                                    detailsHeaderProps.selectAllVisibility = SelectAllVisibility.hidden;
                                }

                                return (
                                    <Sticky stickyPosition={StickyPositionType.Header} stickyBackgroundColor="transparent">
                                        {defaultRender && defaultRender(detailsHeaderProps)}
                                    </Sticky>
                                )
                            }} />
                </ScrollablePane>
            </div>
        );
    }

    /**
     * Sets the sticky list container ref
     */
    private setStickyListContainerRef = (container: HTMLDivElement) => {
        this._stickyListContainer = container;

        if (!container) {
            return;
        }

        this.handleScroll({
            target: {
                scrollLeft: container.scrollLeft
            }
        });
    }

    /**
     * Handles the scroll event
     * @param event Event
     */
    private handleScroll = (event: any): void => {
        if (!this._stickyListContainer) {
            return;
        }

        let element = this._stickyListContainer.querySelector("[class*='stickyAbove-']") as HTMLDivElement;
        if (element != null) {
            element.scrollLeft = event.target.scrollLeft;
            element.style.overflowX = "hidden";
        }
    }

    /**
     * Returns the sticky container height
     * @returns Height of the container
     */
    private getStickyContainerHeight = (): number => {
        if (this.props.itemOverwrittenHeight) {
            return Math.max(61, this.props.itemOverwrittenHeight);
        }

        let height: number = 61;
        if (this.props.items) {
            height += this.props.items.length * (this.props.overwrittenRowHeight ? this.props.overwrittenRowHeight : 58);
        }

        if(this._stickyListContainer && this.props.autoCalcContentHeight) {
            let viewPort = this._stickyListContainer.querySelector(".ms-Viewport") as HTMLDivElement;
            if(viewPort) {
                let viewPortHeight = this.props.items.length > 0 ? Math.max(viewPort.offsetHeight + this._autoHeightAdd, height) : viewPort.offsetHeight + this._autoHeightAdd;
                return Math.min(isIE11() ? this._maxHeightIE11 : this._maxHeight, viewPortHeight);
            }
        }

        return Math.min(isIE11() ? this._maxHeightIE11 : this._maxHeight, height);
    }

    /**
     * Checks if a uncesseary horizontal scroll is needed
     * @returns true if the horizontal scroll fix is needed
     */
    private isPreventUncessearyHorizontalScrollNeeded = (): boolean => {
        if (!this._stickyListContainer) {
            return false;
        }

        let containerWidth = this._stickyListContainer.offsetWidth;
        let totalWidth = 0;
        if (this.props.columns) {
            for (let curItem of this.props.columns) {
                totalWidth += curItem.maxWidth ? curItem.maxWidth : curItem.minWidth;
            }
        }

        return totalWidth < containerWidth;
    }

}